import { usePage } from '@inertiajs/react';
import { rem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconError404, IconHandFinger } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

const isCanceledError = (error) =>
    Boolean(error) && (
        error.code === 'ERR_CANCELED'
        || error.name === 'CanceledError'
        || error.name === 'AbortError'
        || (typeof axios?.isCancel === 'function' && axios.isCancel(error))
    );

export const mutateAdUpdate = ({ adId }) => {
    // Status messages in the user's language, not the server's English text.
    const { t } = useTranslation();

    const { config, canEdit = true } = usePage().props;
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationKey: ['ad-update', adId],
        mutationFn: async (variables) => {
            // `__signal` lets a caller supersede an in-flight save (latest-wins auto
            // preview); `__silent` keeps background renders from raising toasts.
            const { __signal: signal, __silent: silent, ...formData } = variables ?? {};

            // AD-187: a view-only ad (see useViewOnlyNotice) is never sent. The save
            // resolves without a request (callers can tell from `viewOnly`), so autosave
            // does not hit a 403 and Continue on the first booking steps, which waits
            // for the save, still moves on. Delivery and later steps write campaign
            // data, which the API refuses for an ad the account may not edit.
            if (canEdit === false) {
                return { data: {}, viewOnly: true };
            }

            const res = await axios.put(route('studio.editor.api.ads.update', { ad: adId }), formData, signal ? { signal } : {});
            return res;
        },
        onMutate: async (formData) => {
            
        },
        onSuccess: async (response, variables) => {

            if (response?.viewOnly) return;

            const data = response?.data;

            // The booking flow reads the clickout type and the preview urls from the v1 show
            // endpoint, so the stepper only learns about a new Landingpage step from here.
            queryClient.invalidateQueries({ queryKey: ['api-v1-studio-get-ad'] });

            if (variables?.__silent) return;

            if (!data?.previewUrl) {
                notifications.show({
                    color: 'green',
                    title: t('ads.toast.saved'),
                })
            }
            
            if (data?.ad?.ad_showcase?.id && data?.ad?.status === 4) {
                
                const id = notifications.show({
                    loading: true,
                    title: 'Showcase ',
                    message: 'System is generating showcase image static preview!',
                    autoClose: false,
                    withCloseButton: false,
                });

                try {
                    const cacheId = data?.ad?.updated_at ? Date.parse(data?.ad?.updated_at) || Date.now() : Date.now();

                    const imageUrl = config?.adProcessingUrl + data?.ad?.hash + `?cacheId=${cacheId}`;
                    
                    const response = await axios.get(imageUrl, { responseType: 'blob' });

                    if (response.status === 204) {
                        throw new Error('');
                    }

                    notifications.update({
                        id,
                        color: 'green',
                        title: 'Preview is generated successfully',
                        icon: <IconCheck style={{ width: rem(18), height: rem(18) }} />,
                        loading: false,
                        autoClose: 2000,
                    });

                } catch (error) {

                    notifications.update({
                        id,
                        color: 'red',
                        title: 'Failed to generate Preview',
                        icon: <IconError404 style={{ width: rem(18), height: rem(18) }} />,
                        loading: false,
                        autoClose: 2000,
                    });
                }
            }
        },
        onError: async (error, variables) => {

            if (isCanceledError(error) || variables?.__silent) return;

            notifications.show({
                color: 'red',
                title: t('common.error'),
                message: error?.response?.data?.message || "",
            })
        }
    });

    return mutation;
}